import { PlayerDetailSeasonCategoricalScoresFragment } from "@/generated/graphql";

/**
 * Metric labels that should not be clickable in the UI
 * These metrics have special behavior and should not trigger the standard metric selection flow
 */
export const NON_CLICKABLE_METRICS = [
  "Transfer Value",
  "Months Remaining",
  "Gemini Player Valuation",
];

/**
 * Check if a metric label should be clickable
 * @param label - The metric label to check
 * @returns true if the metric should be clickable, false otherwise
 */
export const isClickableMetric = (label: string): boolean => {
  return !NON_CLICKABLE_METRICS.includes(label);
};

/**
 * Metric ids whose explanation modal renders the categorical over-time chart
 * instead of the roster-comparison DotPlot, mapped to the
 * `PlayerSeasonCategoricalScore` field each one plots.
 * @remarks
 * SE-7449: this map is the SINGLE source of truth for "which metrics are
 * categorical". `MetricExplanationContentContainer` used to repeat the six ids
 * in a `switch` alongside a separate `CATEGORICAL_METRIC_IDS` array, so adding
 * a seventh categorical metric to only one of them made the explanation modal
 * render a roster DotPlot AND no category chart. Both the id list and the
 * score-key lookup are now derived from this one object, which makes that
 * particular disagreement unrepresentable.
 */
export const CATEGORICAL_METRIC_SCORE_KEYS = {
  carrying: "carryingTransPositionalCategoricalScore",
  dribbling: "dribblingTransPositionalCategoricalScore",
  passing: "passingTransPositionalCategoricalScore",
  shooting: "shotLocationTransGlobalCategoricalScore",
  shot_stopping: "gkShotStoppingTransCategoricalScore",
  gk_passing: "gkPassingTransCategoricalScore",
} as const satisfies Record<
  string,
  keyof PlayerDetailSeasonCategoricalScoresFragment
>;

/**
 * A metric id that renders the categorical over-time chart.
 */
export type CategoricalMetricId = keyof typeof CATEGORICAL_METRIC_SCORE_KEYS;

/**
 * Metric ids whose explanation modal renders the categorical over-time chart
 * instead of the roster-comparison DotPlot.
 */
export const CATEGORICAL_METRIC_IDS: readonly string[] = Object.keys(
  CATEGORICAL_METRIC_SCORE_KEYS
);

/**
 * Narrows an arbitrary metric id to one that has a categorical over-time chart.
 * @param metricId - The metric id to test.
 * @returns Whether the id has a categorical score key, narrowing it when so.
 */
export const isCategoricalMetricId = (
  metricId: string
): metricId is CategoricalMetricId =>
  Object.hasOwn(CATEGORICAL_METRIC_SCORE_KEYS, metricId);
